<template>
  <StackLayout :class="classes">
    <StackLayout
      ref="label"
      class="label"
      orientation="horizontal"
      @loaded="labelLoaded"
      @tap="labelAction"
      @click="labelAction"
    >
      <Label 
        :for="inputId"
        :text="label" 
        verticalAlignment="center" 
      />
    </StackLayout>
    <GridLayout 
      v-if="invalid" 
      class="error" 
      columns="30, *"
      :id="errorId"
      role="alert"
      aria-live="polite"
    >
      <AgIcon 
        col="0" 
        type="exclamationCircle" 
        :color="errorColor" 
        size="16" 
        aria-hidden="true"
      />
      <Label 
        col="1" 
        class="text" 
        :text="error" 
      />
    </GridLayout>
    <AbsoluteLayout>
      <GridLayout
        class="wrapper"
        :columns="columns"
        orientation="horizontal"
        @tap="handleWrapperTap"
        @click="handleWrapperClick"
      >
        <template v-if="type === 'text' || type === 'number'">
          <TextField
            col="0"
            ref="input"
            :class="'input' + (filled ? ' filled' : '')"
            :id="inputId"
            :text="value"
            :keyboardType="keyboardTypeReal"
            :secure="secure && !visibility"
            :maxLength="parseInt(maxLength)"
            :editable="!readonly"
            :aria-label="label"
            :aria-describedby="ariaDescribedBy"
            :aria-invalid="invalid ? 'true' : 'false'"
            :aria-errormessage="invalid ? errorId : null"
            :autocomplete="autocompleteValue"
            @textChange="setValue($event.value)"
            @focus="onFocus"
            @blur="onBlur"
          />
          <AgIcon
            col="1"
            v-if="info"
            type="info"
            role="button"
            :aria-label="'Information zu ' + label"
            tabindex="0"
            @tap="onInfo"
            @click="onInfo"
            @keydown="handleInfoKeydown"
          />
          <AgIcon
            :col="info ? 2 : 1"
            v-if="secure"
            :type="visibility ? 'eye' : 'eyeSlash'"
            role="button"
            :aria-label="visibility ? 'Passwort verbergen' : 'Passwort anzeigen'"
            tabindex="0"
            @tap="toggleVisibility"
            @click="toggleVisibility"
            @keydown="handleVisibilityKeydown"
          />
          <AgIcon
            :col="info ? 2 : 1"
            v-if="readonly"
            type="ban"
            color="#C6C6C6"
            aria-hidden="true"
          />
        </template>
        <template v-if="type === 'date'">
          <TextField
            col="0"
            ref="dateInput"
            :class="'input' + (filled ? ' filled' : '')"
            :id="inputId"
            :text="displayDateText"
            placeholder="TT.MM.JJJJ"
            :maxLength="10"
            :aria-label="label"
            :aria-describedby="ariaDescribedBy"
            :aria-invalid="invalid ? 'true' : 'false'"
            :aria-errormessage="invalid ? errorId : null"
            @textChange="handleDateInput"
            @focus="onFocus"
            @blur="onDateBlur"
          />
          <AgIcon
            col="1"
            v-if="info"
            type="info"
            role="button"
            :aria-label="'Information zu ' + label"
            tabindex="0"
            @tap="onInfo"
            @click="onInfo"
            @keydown="handleInfoKeydown"
          />
          <AgIcon 
            :col="info ? 2 : 1" 
            type="far:calendar"
            role="button"
            aria-label="Kalender öffnen"
            tabindex="0"
            @click="handleCalendarClick" 
            @keydown="handleCalendarKeydown"
            class="calendar-icon"
          />
        </template>
        <template v-if="type === 'select'">
          <Label 
            col="0" 
            :class="'input' + (filled ? ' filled' : '')"
            :text="selectionText || 'Bitte wählen'" 
            :textWrap="textWrap" 
            :id="inputId"
            role="button"
            :aria-label="label + ': ' + selectionText"
            :aria-describedby="ariaDescribedBy"
            :aria-expanded="false"
            tabindex="0"
            @keydown="handleSelectKeydown"
            ref="selectInput"
          />
          <AgIcon
            col="1"
            v-if="info"
            type="info"
            role="button"
            :aria-label="'Information zu ' + label"
            tabindex="0"
            @tap="onInfo"
            @click="onInfo"
            @keydown="handleInfoKeydown"
          />
          <AgIcon 
            :col="info ? 2 : 1" 
            type="bars" 
            role="button"
            aria-label="Auswahl öffnen"
            tabindex="0"
            @click="handleSelectIconClick"
            @keydown="handleSelectIconKeydown"
            class="select-icon"
          />
        </template>
        <template v-if="type === 'multiline'">
          <TextView
            col="0"
            ref="input"
            class="input"
            :id="inputId"
            :text="value"
            :maxLength="parseInt(maxLength)"
            :aria-label="label"
            :aria-describedby="ariaDescribedBy"
            :aria-invalid="invalid ? 'true' : 'false'"
            :aria-errormessage="invalid ? errorId : null"
            @textChange="setValue($event.value)"
            @focus="onFocus"
            @blur="onBlur"
          />
          <AgIcon
            col="1"
            v-if="info"
            type="info"
            role="button"
            :aria-label="'Information zu ' + label"
            tabindex="0"
            @tap="onInfo"
            @click="onInfo"
            @keydown="handleInfoKeydown"
          />
        </template>
        <template v-if="type === 'checkbox'">
          <Label 
            col="0" 
            class="input" 
            :text="checkboxText" 
            :textWrap="textWrap" 
            :id="inputId"
            role="button"
            :aria-label="label + ': ' + checkboxText"
            :aria-describedby="ariaDescribedBy"
            tabindex="0"
            @keydown="handleCheckboxKeydown"
            ref="checkboxInput"
          />
          <AgIcon
            col="1"
            v-if="info"
            type="info"
            role="button"
            :aria-label="'Information zu ' + label"
            tabindex="0"
            @tap="onInfo"
            @click="onInfo"
            @keydown="handleInfoKeydown"
          />
          <AgIcon 
            :col="info ? 2 : 1" 
            type="bars" 
            role="button"
            aria-label="Auswahl öffnen"
            tabindex="0"
            @click="handleCheckboxIconClick"
            @keydown="handleCheckboxIconKeydown"
            class="checkbox-icon"
          />
        </template>
      </GridLayout>
    </AbsoluteLayout>
  </StackLayout>
</template>

<script>
import { colors } from "@/helpers/ui";
import dialog from "@/helpers/dialog";
import locale from "@/locales";
import AgModalDate from "@/components/Modal/Date";
import { generateUniqueId } from "@/helpers/validation-helper";

const isWeb = process.env.VUE_APP_MODE === "web";

export default {
  props: {
    label: {
      type: String,
      default: "[label]",
    },
    value: {
      type: [Array, String, Number, Date],
      default: () => {},
    },
    type: {
      type: String,
      default: "text",
    },
    keyboardType: {
      type: String,
    },
    secure: {
      type: Boolean,
      default: false,
    },
    info: {
      type: Boolean,
      default: false,
    },
    maxLength: {
      type: [Number, String],
      default: 9999, // IT'S OVER 9000!!!!!
    },
    trim: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Array,
      default() {
        return [];
      },
    },
    rules: {
      type: Array,
      default() {
        return [];
      },
    },
    textWrap: {},
    autocomplete: {
      type: String,
      default: null,
    },
    describedBy: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      valueSelected: null,
      valueText: "",
      visibility: false,
      focused: false,
      touched: false,
      error: false,
      errorColor: colors.error,
      uid: generateUniqueId('input'),
      rawDateInput: "", // Store the user's raw input for date fields
      previousFocusElement: null, // Store the element that had focus before opening modal
    };
  },
  computed: {
    inputId() {
      return `input-${this.uid}`;
    },
    errorId() {
      return `error-${this.uid}`;
    },
    ariaDescribedBy() {
      const ids = [];
      if (this.describedBy) ids.push(this.describedBy);
      if (this.invalid) ids.push(this.errorId);
      return ids.length > 0 ? ids.join(' ') : null;
    },
    autocompleteValue() {
      if (this.autocomplete) return this.autocomplete;
      
      // Auto-detect common field types
      const labelLower = this.label.toLowerCase();
      if (labelLower.includes('email') || labelLower.includes('e-mail')) return 'email';
      if (labelLower.includes('passwort')) return 'current-password';
      if (labelLower.includes('vorname')) return 'given-name';
      if (labelLower.includes('nachname')) return 'family-name';
      if (labelLower.includes('telefon')) return 'tel';
      if (labelLower.includes('geburtsdatum')) return 'bday';
      if (labelLower.includes('straße')) return 'street-address';
      if (labelLower.includes('postleitzahl')) return 'postal-code';
      if (labelLower.includes('stadt')) return 'address-level2';
      
      return null;
    },
    classes() {
      const classes = ["control", "input-control", "type-" + this.type];

      if (this.filled) classes.push("filled");
      if (this.focused) classes.push("focused");
      if (this.invalid) classes.push("invalid");
      if (this.readonly) classes.push("readonly");
      // Always show label above field
      classes.push("label-above");

      return classes.join(" ");
    },
    columns() {
      let columns = "*";

      if (
        this.type === "date" ||
        this.type === "select" ||
        this.type === "checkbox" ||
        (this.type === "text" && (this.secure || this.readonly))
      ) {
        columns += ", 60";
      }

      if (this.info) {
        columns += ", 60";
      }

      return columns;
    },
    dateText() {
      if (this.value !== undefined && this.value !== null && this.value !== "") {
        return locale.formatDateMedium(this.value);
      } else {
        return "";
      }
    },
    dateInputText() {
      if (this.value !== undefined && this.value !== null && this.value !== "") {
        return this.formatDateInput(this.value);
      } else {
        return "";
      }
    },
    displayDateText() {
      // If user is actively typing, show the formatted raw input
      if (this.focused && this.rawDateInput) {
        const formatted = this.formatRawDateInput(this.rawDateInput);
        return formatted;
      }
      
      // When not focused, show the display format from actual value
      if (this.value !== undefined && this.value !== null && this.value !== "") {
        const formatted = this.formatDateInput(this.value);
        return formatted;
      } else {
        return "";
      }
    },
    selectionText() {
      if (this.selectionIndex >= 0 && this.options[this.selectionIndex].label) {
        return this.options[this.selectionIndex].label;
      } else if (this.value && this.value !== "") {
        return this.value;
      } else {
        return "";
      }
    },
    checkboxText() {
      return this.value && this.value.length > 0
        ? this.value
            .map((v) => this.options.find((o) => o.value === v)?.label || v)
            .join(", ")
        : "Bitte wählen";
    },
    selectionIndex() {
      return this.options.findIndex((item) => item.value === this.value);
    },
    keyboardTypeReal() {
      return this.type === "number" ? "number" : this.keyboardType;
    },
    invalid() {
      if (!this.touched) {
        return false;
      }

      return !this.validate();
    },
    filled() {
      return !!this.value;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(to) {
        if (to === undefined && this.type === "date") {
          let date = new Date();

          date.setHours(0);
          date.setMinutes(0);
          date.setSeconds(0);

          this.setValue(date.toISOString());
        }
      },
    },
    filled() {
      this.checkFilled();
    },
  },
  methods: {
    setValue(to) {
      // Never trim multiline fields to preserve spaces in damage descriptions
      const shouldTrim = this.trim && this.type !== 'multiline';
      this.$emit("input", typeof to === "string" && shouldTrim ? to.trim() : to);
    },
    checkFilled() {
      if (this.filled || !this.focused) {
        this.animateLabel(this.filled, !this.focused);
      }
    },
    labelAction() {
      if (this.readonly) {
        return false;
      }

      if (this.$refs.input) {
        isWeb ? this.$refs.input.focus() : this.$refs.input.nativeView.focus();
      } else if (this.type === "select" || this.type === "checkbox") {
        this.startSelect();
      } else if (this.type === "date") {
        // Don't open date picker when clicking on label
        // Only open via calendar icon
        if (this.$refs.dateInput) {
          isWeb ? this.$refs.dateInput.focus() : this.$refs.dateInput.nativeView.focus();
        }
      }
    },
    labelLoaded() {
      this.checkFilled();
    },
    handleWrapperTap(event) {
      // Check if the tap originated from the calendar icon, select icon, or checkbox icon
      if (event.object && event.object.className && 
          (event.object.className.includes('calendar-icon') || 
           event.object.className.includes('select-icon') ||
           event.object.className.includes('checkbox-icon'))) {
        return; // Let the icon handle this
      }
      this.wrapperAction();
    },
    handleWrapperClick(event) {
      // Check if the click originated from the calendar icon, select icon, or checkbox icon
      if (event.target && event.target.closest && 
          (event.target.closest('.calendar-icon') || 
           event.target.closest('.select-icon') ||
           event.target.closest('.checkbox-icon'))) {
        return; // Let the icon handle this
      }
      this.wrapperAction();
    },
    wrapperAction() {
      this.$emit("click");
      if (this.readonly) {
        return false;
      }

      switch (this.type) {
        case "select":
          this.startSelect();
          break;

        case "checkbox":
          this.startCheckboxSelect();
          break;

        case "date":
          // Don't open date picker when clicking on text field wrapper
          // Only open via calendar icon - just focus the input
          if (this.$refs.dateInput) {
            isWeb ? this.$refs.dateInput.focus() : this.$refs.dateInput.nativeView.focus();
          }
          break;
      }
    },
    onFocus() {
      if (!this.readonly) {
        this.focused = true;
        this.animateLabel(true);
        
        // For date inputs, initialize rawDateInput with current digits
        if (this.type === 'date' && this.dateInputText) {
          this.rawDateInput = this.dateInputText.replace(/[^\d]/g, '');
        }
        
        this.$emit("focus");
      }
    },
    onBlur() {
      this.focused = false;
      this.touched = true;
      this.animateLabel(this.filled);
      this.$emit("blur");
    },
    animateLabel(on, direct) {
      if (isWeb) return;

      const view = this.$refs.label.nativeView;
      const translate = on ? { x: 2, y: -30 } : { x: 0, y: 0 };
      const scale = on ? { x: 0.8, y: 0.8 } : { x: 1, y: 1 };

      if (direct) {
        view.translateX = translate.x;
        view.translateY = translate.y;
        view.scaleX = scale.x;
        view.scaleY = scale.y;
      } else {
        view.animate({ translate, scale, duration: 200 });
      }
    },
    toggleVisibility() {
      this.visibility = !this.visibility;
    },
    onInfo(event) {
      this.$emit("info", event);
    },
    startCheckboxSelect() {
      // Store the current focus element before opening modal
      if (this.type === "checkbox" && this.$refs.checkboxInput) {
        this.previousFocusElement = isWeb ? 
          (this.$refs.checkboxInput.$el || this.$refs.checkboxInput) : 
          this.$refs.checkboxInput.nativeView;
      }

      this.focused = true;

      dialog
        .checkbox(this.label, "Abbrechen", this.options, this.value)
        .then((result) => {
          if (result) {
            this.setValue(result);
          }

          // Restore focus to the element that opened the modal
          this.$nextTick(() => {
            this.touched = true;
            this.focused = false;
            
            // Use a timeout to ensure modal is fully closed before restoring focus
            setTimeout(() => {
              // Try to focus back on the checkbox icon if it opened the modal
              const checkboxIcon = this.$el.querySelector('.checkbox-icon');
              if (checkboxIcon) {
                checkboxIcon.focus();
              } else if (this.previousFocusElement && this.previousFocusElement.focus) {
                try {
                  this.previousFocusElement.focus();
                } catch (error) {
                  // If focus fails, just continue without error
                  console.warn('Failed to restore focus to checkbox element:', error);
                }
              }
              this.previousFocusElement = null;
            }, 150);
          });
        });
    },
    startSelect() {
      // Store the current focus element before opening modal
      if (this.type === "select" && this.$refs.selectInput) {
        this.previousFocusElement = isWeb ? 
          (this.$refs.selectInput.$el || this.$refs.selectInput) : 
          this.$refs.selectInput.nativeView;
      } else if (this.type === "checkbox" && this.$refs.checkboxInput) {
        this.previousFocusElement = isWeb ? 
          (this.$refs.checkboxInput.$el || this.$refs.checkboxInput) : 
          this.$refs.checkboxInput.nativeView;
      }

      this.onFocus();

      if (this.type === "checkbox") {
        this.startCheckboxSelect();
      } else {
        dialog.action(this.label, "Abbrechen", this.options).then((result) => {
          if (result) {
            this.setValue(result.value);
          }

          // Restore focus to the element that opened the modal
          this.$nextTick(() => {
            this.touched = true;
            this.focused = false;
            
            // Use a timeout to ensure modal is fully closed before restoring focus
            setTimeout(() => {
              // Try to focus back on the select icon if it opened the modal
              const selectIcon = this.$el.querySelector('.select-icon');
              if (selectIcon) {
                selectIcon.focus();
              } else if (this.previousFocusElement && this.previousFocusElement.focus) {
                try {
                  this.previousFocusElement.focus();
                } catch (error) {
                  // If focus fails, just continue without error
                  console.warn('Failed to restore focus to select element:', error);
                }
              }
              this.previousFocusElement = null;
            }, 150);
          });
        }); //.catch(() => {})
      }
    },
    pickDate() {
      this.focused = true;

      dialog
        .component(AgModalDate, {
          caption: this.label,
          date: this.value || new Date(),
        })
        .then((result) => {
          this.focused = false;

          if (result) {
            this.setValue(locale.formatDateLocalISO(result));
          }
          
          // Ensure proper focus restoration after modal closes
          this.$nextTick(() => {
            // Try to focus back on the calendar icon that opened the modal
            const calendarIcon = this.$el.querySelector('.calendar-icon');
            if (calendarIcon) {
              calendarIcon.focus();
            } else if (this.$refs.dateInput) {
              // Fallback to the date input field
              if (isWeb) {
                const element = this.$refs.dateInput.$el || this.$refs.dateInput;
                if (element) element.focus();
              } else {
                this.$refs.dateInput.nativeView.focus();
              }
            }
          });
        });
    },
    validate() {
      for (let i = 0; i < this.rules.length; ++i) {
        const result = this.rules[i](this.value);

        if (result !== true) {
          this.error = result;

          return false;
        }
      }

      return true;
    },
    validateByForm() {
      this.touched = true;

      return !this.invalid;
    },
    formatDateInput(date) {
      if (!date) return "";
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      if (isNaN(dateObj.getTime())) return "";
      
      const day = String(dateObj.getDate()).padStart(2, '0');
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const year = dateObj.getFullYear();
      
      return `${day}.${month}.${year}`;
    },
    parseDateInput(dateString) {
      if (!dateString) return null;
      
      // Remove any non-digit characters except dots
      const cleaned = dateString.replace(/[^\d.]/g, '');
      
      // Check for various date formats
      const patterns = [
        /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/, // DD.MM.YYYY
        /^(\d{1,2})\.(\d{1,2})\.(\d{2})$/, // DD.MM.YY
        /^(\d{1,2})\.(\d{1,2})$/, // DD.MM (current year)
      ];
      
      for (const pattern of patterns) {
        const match = cleaned.match(pattern);
        if (match) {
          const day = parseInt(match[1], 10);
          const month = parseInt(match[2], 10);
          let year = match[3] ? parseInt(match[3], 10) : new Date().getFullYear();
          
          // Convert 2-digit year to 4-digit
          if (year < 100) {
            year += year < 50 ? 2000 : 1900;
          }
          
          // Validate date ranges
          if (month < 1 || month > 12 || day < 1 || day > 31) {
            return null;
          }
          
          const date = new Date(year, month - 1, day);
          
          // Check if the date is valid (handles invalid dates like Feb 30)
          if (date.getDate() === day && date.getMonth() === month - 1 && date.getFullYear() === year) {
            return date.toISOString();
          }
        }
      }
      
      return null;
    },
    formatRawDateInput(rawInput) {
      // Only allow digits - remove any non-digit characters
      const digitsOnly = rawInput.replace(/[^\d]/g, '');
      
      // Limit to 8 digits max (DDMMYYYY)
      const limitedDigits = digitsOnly.slice(0, 8);
      
      // Format with dots based on length
      if (limitedDigits.length <= 2) {
        // Just day: "12"
        return limitedDigits;
      } else if (limitedDigits.length <= 4) {
        // Day + month: "1207" -> "12.07"
        return limitedDigits.slice(0, 2) + '.' + limitedDigits.slice(2);
      } else {
        // Day + month + year: "12072025" -> "12.07.2025"
        return limitedDigits.slice(0, 2) + '.' + limitedDigits.slice(2, 4) + '.' + limitedDigits.slice(4);
      }
    },
    handleInfoKeydown(event) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.onInfo(event);
      }
    },
    handleVisibilityKeydown(event) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.toggleVisibility();
      }
    },
    handleDateKeydown(event) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.pickDate();
      }
    },
    handleCalendarKeydown(event) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        event.stopPropagation();
        this.pickDate();
      }
    },
    handleCalendarClick(event) {
      event.stopPropagation();
      event.preventDefault();
      this.pickDate();
    },
    handleDateInput(event) {
      const inputValue = event.value || '';
      
      // Store only digits from the input
      this.rawDateInput = inputValue.replace(/[^\d]/g, '');
      
      // Don't validate or set value while typing - just update display
      // Only clear if completely empty
      if (this.rawDateInput.length === 0) {
        this.setValue("");
      }
    },
    onDateBlur() {
      this.focused = false;
      this.touched = true;
      this.animateLabel(this.filled);
      
      // Validate and set the date when user finishes typing
      if (this.rawDateInput.length >= 4) {
        const formattedInput = this.formatRawDateInput(this.rawDateInput);
        const parsedDate = this.parseDateInput(formattedInput);
        if (parsedDate) {
          this.setValue(parsedDate);
        }
      } else if (this.rawDateInput.length === 0) {
        this.setValue("");
      }
      
      // Clear raw input when not focused
      this.rawDateInput = "";
      
      this.$emit("blur");
    },
    handleSelectKeydown(event) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.startSelect();
      }
    },
    handleCheckboxKeydown(event) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.startCheckboxSelect();
      }
    },
    handleSelectIconClick(event) {
      event.stopPropagation();
      event.preventDefault();
      this.startSelect();
    },
    handleSelectIconKeydown(event) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        event.stopPropagation();
        this.startSelect();
      }
    },
    handleCheckboxIconClick(event) {
      event.stopPropagation();
      event.preventDefault();
      this.startCheckboxSelect();
    },
    handleCheckboxIconKeydown(event) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        event.stopPropagation();
        this.startCheckboxSelect();
      }
    },
  },
  mounted() {
    if (isWeb) {
      this.checkFilled();
      
      // Add native keyboard event listeners for date and select inputs
      this.$nextTick(() => {
        // Don't add date picker keyboard listeners to the text input
        // Only the calendar icon should open the date picker
        
        if (this.type === 'select' && this.$refs.selectInput) {
          const element = this.$refs.selectInput.$el || this.$refs.selectInput;
          if (element) {
            element.addEventListener('keydown', (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                this.startSelect();
              }
            });
          }
        }
        
        if (this.type === 'checkbox' && this.$refs.checkboxInput) {
          const element = this.$refs.checkboxInput.$el || this.$refs.checkboxInput;
          if (element) {
            element.addEventListener('keydown', (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                this.startCheckboxSelect();
              }
            });
          }
        }
        
        // Calendar icon keyboard support is handled by Vue @keydown handler
        // No need for additional native event listeners
      });
    } else {
      let view = this.$refs.label.nativeView;

      view.originX = 0;
      view.originY = 0.5;
    }
  },
};
</script>

<style scoped lang="scss">
.control {
  margin-bottom: 10px;
}

.wrapper {
  border-style: solid;
  border-color: #6D6D6D;
  background-color: #ffffff;
  padding: 0;
  width: 100%;
  border-radius: 8px;
  border-width: 1px;
  margin-top: 10px;
}

.wrapper:hover {
  border-width: 2px;
  border-color: #0066CB;
}

.focused .wrapper {
  border-color: #004e9e;
  box-shadow: 0px 0px 0px 2px #FFD448, 0px 0px 0px 4px #0A3B70;
}

.invalid .wrapper {
  border-color: #C90C0F;
}

.readonly .wrapper {
  border-color: #D1D1D1;
  background-color: #E7E7E7;
}

.label {
  font-family: "Inter", "Open Sans", sans-serif;
  font-weight: 400;
  color: #3B3B3B;
  background-color: #fff;
  transform-origin: 0 50%;
  font-size: 16px;
  line-height: 24px;
}

.focused .label,
.filled.focused .label {
  color: #3B3B3B;
  font-weight: 400;
}

.invalid.focused .label,
.invalid.filled.focused .label {
  color: #C90C0F;
}

.filled .label,
.filled.readonly .label {
  color: #3B3B3B;
  font-weight: 400;
}

.input {
  font-family: "Inter", "Open Sans", sans-serif;
  color: #6D6D6D; /* Improved placeholder color for accessibility */
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
}

.input.filled {
  color: #3B3B3B; /* Primary text color when filled */
}

.visibility {
  background-color: silver;
  color: #004e9e;
}

.error {
  color: #C90C0F;
  
}

.error .text {
  font-family: "Inter", "Open Sans", sans-serif;
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  color: #C90C0F;
}

.ns-ios,
.ns-android {
  .wrapper {
    border-width: 2;
    height: 60;
    border-radius: 6;
    margin-top: 12;
  }

  .type-multiline .wrapper {
    height: 200;
  }

  .type-multiline .input {
    padding-top: 16;
    padding-bottom: 16;
  }

  .label {
    font-size: 17;
    height: 26;
    line-height: 26;
    border-radius: 12;
    padding: 0 7;
  }

  &.ns-ios .label {
    font-size: 19;
  }

  .input {
    font-size: 17;
    padding: 0 16;
    border-bottom-width: 1;
    border-bottom-color: transparent;
  }

  &.ns-ios .input {
    font-size: 19;
  }

  .type-date .input,
  .type-select .input,
  .type-checkbox .input {
    line-height: 60;
    vertical-align: center;
  }


  .type-multiline .input {
    padding-top: 16;
    padding-bottom: 16;
  }

  .error {
    margin-top: 10;
  }

  .error .text {
    font-size: 16;
  }

  &.ns-ios .error .text {
    font-size: 19;
  }
}

html {
  .wrapper {
    border-width: 2px;
    height: 60px;
    border-radius: 8px;
    margin-top: 10px;
  }

  .calendar-icon,
  .select-icon,
  .checkbox-icon {
    background-color: #004e9e !important;
    color: white !important;
    border-radius: 0 6px 6px 0;
    padding: 16px 18px;
    margin: 0;
    height: 100%;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:focus {
      outline: 3px solid #FFD448;
      outline-offset: 2px;
    }
  }
  
  .wrapper:hover {
    border-width: 2px;
    border-color: #0066CB;
  }
  
  .focused .wrapper {
    box-shadow: 0px 0px 0px 2px #FFD448, 0px 0px 0px 4px #0A3B70;
  }

  .type-multiline .wrapper {
    height: 200px;
  }

  .label {
    font-size: 16px;
    height: 24px;
    line-height: 24px;
    margin-bottom: 4px;
    color: #3B3B3B;
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
  }

  .focused .label {
    color: #3B3B3B;
  }

  .filled:not(.focused) .label {
    transition: transform 0ms;
  }

  .type-text .label,
  .type-multiline .label {
    cursor: text;
  }

  .input {
    padding: 0 16px;
    font-size: 16px;
    color: #6D6D6D;
    font-family: "Inter", "Open Sans", sans-serif;
    font-weight: 400;
    line-height: 24px;
  }

  .input.filled {
    color: #3B3B3B;
  }

  .type-date .input,
  .type-select .input,
  .type-checkbox .input {
    line-height: 60px;
    
  }


  .type-multiline .input {
    padding-top: 16px;
    padding-bottom: 16px;
    white-space: pre-wrap; /* Preserve spaces and line breaks */
  }

  .error {
    margin-top: 10px;
  }

  .error .text {
    font-family: "Inter", "Open Sans", sans-serif;
    font-size: 13px;
    font-weight: 400;
    line-height: 18px;
    color: #C90C0F;
    text-overflow: ellipsis;
    overflow: hidden;
  }
}
</style>
